import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OrderService from '../services/orderService';
import { Order } from '../types';
// CSS đã được hợp nhất vào App.css
import LogService from '../services/logService';
import AuthService from '../services/authService';
import moment from 'moment-timezone';
import { formatUTCToLocal } from '../utils/timezoneUtils';

const OrderList: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Bộ lọc
  const [customerQuery, setCustomerQuery] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);

  // Thêm state cho popup
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState<string | null>(null);

  // State cho modal phóng to hình ảnh
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Thêm state cho sắp xếp
  const [sortMode, setSortMode] = useState<'default' | 'deliveryDate'>('deliveryDate');
  const [deliveryDateSortDirection, setDeliveryDateSortDirection] = useState<'asc' | 'desc' | 'default'>('default');

  // Thêm useEffect để cập nhật meta viewport cho thiết bị di động
  useEffect(() => {
    // Tìm meta viewport tag hiện có
    let metaViewport = document.querySelector('meta[name="viewport"]');

    // Nếu không tìm thấy, tạo mới
    if (!metaViewport) {
      metaViewport = document.createElement('meta');
      metaViewport.setAttribute('name', 'viewport');
      document.head.appendChild(metaViewport);
    }

    // Cập nhật nội dung meta viewport
    metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');

    // Cleanup khi component unmount
    return () => {
      // Nếu muốn khôi phục lại giá trị ban đầu khi component unmount
      metaViewport?.setAttribute('content', 'width=device-width, initial-scale=1.0');
    };
  }, []);

  // Effect để phát hiện thiết bị mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Thêm hiệu ứng trực quan khi sort thay đổi
  useEffect(() => {
    if (sortMode !== 'default') {
      // Tạo hiệu ứng khi thay đổi chế độ sắp xếp
      const tableRows = document.querySelectorAll('.order-row');
      tableRows.forEach((row) => {
        row.classList.add('sorting-animation');
        setTimeout(() => {
          row.classList.remove('sorting-animation');
        }, 1000);
      });
    }
  }, [sortMode]);

  // Load orders on mount and when filters change
  useEffect(() => {
    // Luôn gọi searchOrders để xử lý cả trường hợp có bộ lọc và không có bộ lọc
    searchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuery, status, startDate, endDate]);

  // Thêm effect để ẩn thông báo copy sau 2 giây
  useEffect(() => {
    if (copyMessage) {
      const timer = setTimeout(() => {
        setCopyMessage(null);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copyMessage]);

  // Thêm effect để ẩn thông báo cập nhật trạng thái sau 2 giây
  useEffect(() => {
    if (statusUpdateMessage) {
      const timer = setTimeout(() => {
        setStatusUpdateMessage(null);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [statusUpdateMessage]);

  // Thêm effect để lắng nghe phím ESC để đóng modal phóng to hình ảnh
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showImageModal) {
        closeImageModal();
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleKeyDown);
      // Ngăn scroll body khi modal mở
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showImageModal]);

  // Hàm định dạng tiền tệ
  const formatCurrency = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return '0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString('vi-VN');
  };

  // Hàm xử lý copy vào clipboard
  const handleCopy = (text: string, field: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyMessage(`Đã copy ${field}`);
        setTimeout(() => setCopyMessage(null), 2000);
      })
      .catch(err => {
        console.error('Không thể sao chép: ', err);
        setCopyMessage('Lỗi khi sao chép');
      });
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await OrderService.getAll();
      setOrders(response.data);
      setError(null);
    } catch (err) {
      setError('Không thể tải danh sách đơn hàng. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchOrders = async () => {
    const params: any = {};

    // Chỉ thêm các tham số nếu có giá trị
    if (customerQuery && customerQuery.trim()) params.customerQuery = customerQuery.trim();
    if (status && status !== '') params.status = status;
    if (startDate && startDate !== '') params.startDate = startDate;
    if (endDate && endDate !== '') params.endDate = endDate;

    try {
      setLoading(true);
      
      // Nếu không có bộ lọc nào được áp dụng, tải tất cả đơn hàng
      if (Object.keys(params).length === 0) {
        const response = await OrderService.getAll();
        setOrders(response.data);
      } else {
        const response = await OrderService.search(params);
        setOrders(response.data);
      }
      
      setError(null);
    } catch (err) {
      setError('Không thể tìm kiếm đơn hàng. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  // Hàm xử lý sắp xếp cột ngày giao
  const handleDeliveryDateSort = (e: React.MouseEvent<HTMLTableCellElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Chuyển đổi giữa các trạng thái: default -> asc -> desc -> default
    let newDirection: 'asc' | 'desc' | 'default';
    switch (deliveryDateSortDirection) {
      case 'default':
        newDirection = 'asc';
        break;
      case 'asc':
        newDirection = 'desc';
        break;
      case 'desc':
        newDirection = 'default';
        break;
      default:
        newDirection = 'asc';
    }

    setDeliveryDateSortDirection(newDirection);
    setSortMode(newDirection === 'default' ? 'default' : 'deliveryDate');
  };

  // Hàm lấy danh sách đơn hàng đã sắp xếp
  const getSortedOrders = () => {
    if (sortMode === 'default' || deliveryDateSortDirection === 'default') {
      return [...orders]; // Trả về danh sách gốc
    } else {
      // Sắp xếp theo ngày giao
      return [...orders].sort((a, b) => {
        const dateA = new Date(a.deliveryDate);
        const dateB = new Date(b.deliveryDate);

        if (deliveryDateSortDirection === 'asc') {
          return dateA.getTime() - dateB.getTime(); // Tăng dần (cũ -> mới)
        } else {
          return dateB.getTime() - dateA.getTime(); // Giảm dần (mới -> cũ)
        }
      });
    }
  };

  const getImageUrl = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined;
    return OrderService.getImageUrl(imagePath) || undefined;
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa đơn hàng này?')) {
      try {
        await OrderService.delete(id);
        loadOrders();
      } catch (err) {
        setError('Không thể xóa đơn hàng. Vui lòng thử lại sau.');
        console.error(err);
      }
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning';
      case 'preparing':
        return 'bg-primary';
      case 'completed':
        return 'bg-success';
      case 'done':
        return '';
      case 'cancelled':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Chờ xử lý';
      case 'preparing':
        return 'Đang chuẩn bị';
      case 'completed':
        return 'Hoàn thành';
      case 'done':
        return 'Sẵn sàng giao';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return 'Không xác định';
    }
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  const resetFilters = () => {
    setCustomerQuery('');
    setStatus('');
    setStartDate('');
    setEndDate('');
  };

  // Hàm kiểm tra xem có bộ lọc nào đang được áp dụng không
  const hasActiveFilters = () => {
    return !!(customerQuery?.trim() || status || startDate || endDate);
  };

  // Hàm lấy mô tả bộ lọc hiện tại
  const getFilterDescription = () => {
    const filters = [];

    if (customerQuery?.trim()) {
      filters.push(`Tên/SĐT: "${customerQuery.trim()}"`);
    }

    if (status) {
      const statusText = getStatusText(status);
      filters.push(`Trạng thái: ${statusText}`);
    }

    if (startDate) {
      filters.push(`Từ ngày: ${startDate}`);
    }

    if (endDate) {
      filters.push(`Đến ngày: ${endDate}`);
    }

    return filters.length > 0 ? filters.join(', ') : 'Tất cả đơn hàng';
  };

  // Thêm hàm xử lý cho popup
  const openOrderPopup = (order: Order) => {
    setSelectedOrder(order);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedOrder(null);
  };

  // Hàm xử lý phóng to hình ảnh (double click cho desktop, single click cho mobile)
  const handleImageDoubleClick = (imageUrl: string) => (e: React.MouseEvent) => {
    e.stopPropagation(); // Ngăn không cho popup đóng
    setSelectedImageUrl(imageUrl);
    setShowImageModal(true);
  };

  // Hàm xử lý click đơn cho mobile
  const handleImageClick = (imageUrl: string) => (e: React.MouseEvent) => {
    if (isMobile) {
      e.stopPropagation(); // Ngăn không cho popup đóng
      setSelectedImageUrl(imageUrl);
      setShowImageModal(true);
    }
  };

  // Hàm đóng modal phóng to hình ảnh
  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImageUrl(null);
  };

  // Thêm log để debug
  //console.log('selectedOrder:', selectedOrder);
  //console.log('remainingAmount:', selectedOrder?.remainingAmount);
  //console.log('totalValue:', selectedOrder?.totalValue);
  //console.log('deposit:', selectedOrder?.deposit);

  // Thêm hàm xử lý thay đổi trạng thái đơn hàng
  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedOrder) return;

    const newStatus = e.target.value as 'pending' | 'preparing' | 'completed' | 'cancelled' | 'done';
    const oldStatus = selectedOrder.status;

    // Nếu trạng thái không thay đổi, không làm gì cả
    if (newStatus === oldStatus) return;

    try {
      setUpdatingStatus(true);

      // Tạo bản sao của đơn hàng với trạng thái mới
      const updatedOrder = { ...selectedOrder, status: newStatus };

      // Gọi API để cập nhật trạng thái
      await OrderService.update(selectedOrder.id as number, updatedOrder);

      // Cập nhật state của selectedOrder
      setSelectedOrder(updatedOrder);

      // Tạo log cho việc thay đổi trạng thái
      try {
        const currentUser = AuthService.getCurrentUser();
        if (currentUser) {
          const logData = {
            action: 'updated' as 'created' | 'updated' | 'deleted',
            resourceType: 'order' as 'order' | 'customer' | 'staff',
            resourceId: selectedOrder.id as number,
            details: `Cập nhật trạng thái đơn hàng "${selectedOrder.product}" từ ${getStatusText(oldStatus)} sang ${getStatusText(newStatus)}`,
            performedById: currentUser.id as number
          };

          await LogService.create(logData);
        }
      } catch (logError) {
        console.error('Lỗi khi tạo log:', logError);
      }

      // Hiển thị thông báo thành công
      setStatusUpdateMessage('Đã cập nhật trạng thái');

      // Làm mới danh sách đơn hàng
      loadOrders();

      // Tự động ẩn thông báo sau 2 giây
      setTimeout(() => {
        setStatusUpdateMessage(null);
      }, 2000);
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      setStatusUpdateMessage('Lỗi khi cập nhật trạng thái');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Xử lý khi nhấp vào xem chi tiết đơn hàng
  const handleViewOrderDetail = (orderId: number) => {
    navigate(`${userPrefix}/orders/edit/${orderId}`);
  };

  // Xử lý khi nhấp vào sửa đơn hàng
  const handleEditOrder = (orderId: number) => {
    navigate(`${userPrefix}/orders/edit/${orderId}`);
  };

  return (
    <div className="content order-list">
      <div className="row">
        <div className="col-md-12">
          {/* Search section - moved outside of card */}
          <div className="CustomCSS-SearchRow">
            <div className="CustomCSS-SearchInputContainer">
              <input
                type="text"
                className="form-control CustomCSS-SearchInput"
                id="customerQuery"
                placeholder="Nhập tên hoặc số điện thoại"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
            </div>
            <div className="CustomCSS-AddButtonContainer">
              <button
                className="btn btn-info CustomCSS-AddButton"
                onClick={toggleFilterVisibility}
              >
                <i className="fa-solid fa-magnifying-glass"></i> Nâng cao
              </button>
            </div>
          </div>

          {/* Advanced filters section */}
          {isFilterVisible && (
            <div className="card mb-4">
              <div className="card-body">
                <div className="row">
                  <div className="col-12 col-md-4">
                    <div className="form-group mt-3">
                      <label htmlFor="status">Trạng thái</label>
                      <select
                        className="form-control"
                        id="status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="">Tất cả</option>
                        <option value="pending">Chờ xử lý</option>
                        <option value="preparing">Đang chuẩn bị</option>
                        <option value="done">Đã xong</option>
                        <option value="completed">Hoàn thành</option>
                        <option value="cancelled">Đã hủy</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor="startDate">Từ ngày</label>
                      <input
                        type="date"
                        className="form-control"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor="endDate">Đến ngày</label>
                      <input
                        type="date"
                        className="form-control"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4 mt-3 mb-3">
                    <button
                      className="btn btn-danger"
                      onClick={resetFilters}
                      title="Xóa tất cả bộ lọc và hiển thị tất cả đơn hàng"
                    >
                      <i className="nc-icon nc-refresh-69 mr-1"></i> Đặt lại bộ lọc
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table card */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center ">
                {/* Ẩn nút tạo đơn trên mobile, vẫn giữ trên desktop */}
                <Link to={`${userPrefix}/orders/add`} className="btn btn-success btn-lg gap-4 d-none d-lg-inline-flex" style={{ fontSize: '1rem', padding: '12px 24px' }}>
                  <i className="fa-solid fa-plus nc-simple-add mr-1"></i> Tạo đơn
                </Link>
                {/*
                 <button
                  className={`btn ${sortMode === 'default' ? 'btn-outline-primary' : 'btn-primary'} sort-button`}
                  onClick={toggleSortMode}
                  title={sortMode === 'default'
                    ? 'Click để sắp xếp theo ngày giao gần nhất (tính từ hôm nay)'
                    : 'Click để quay lại sắp xếp mặc định'}
                  style={{ zIndex: 100, position: 'relative', fontSize: '1rem', padding: '12px 24px' }}
                >
                  <i className={`fa-solid fa-sort ${sortMode === 'default' ? 'nc-sort-66' : 'nc-minimal-up'} mr-1`}></i>
                  {sortMode === 'default'
                    ? ' Mặc định'
                    : ' Gần nhất'}
                </button>
                */}
                {/* Nút sắp xếp cũ đã được ẩn vì giờ có thể sắp xếp trực tiếp bằng cách click vào cột "Ngày giao" */}
              </div>
            </div>
            <div className="card-body position-relative">

              {error && <div className="alert alert-danger">{error}</div>}


              {loading ? (
                <div className="center-screen">
                  <div className="spinner-border text-primary" role="status" aria-label="loading"></div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="text-primary">
                      <tr>
                        <th>ID</th>
                        <th>Khách hàng</th>
                        <th className="d-none d-md-table-cell">Sản phẩm</th>
                        <th className="d-none d-md-table-cell">Số lượng</th>
                        <th className="d-none d-md-table-cell">Đơn giá</th>
                        <th
                          onClick={handleDeliveryDateSort}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            position: 'relative',
                            paddingRight: '15px'
                          }}
                          title="Click để sắp xếp theo ngày giao"
                        >
                          Ngày giao
                          <span style={{
                            position: 'absolute',
                            right: '1px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '25px',
                            color: deliveryDateSortDirection === 'default' ? '#ccc' : '#007bff'
                          }}>
                            {deliveryDateSortDirection === 'asc' && '↑'}
                            {deliveryDateSortDirection === 'desc' && '↓'}
                            {deliveryDateSortDirection === 'default' && '↕'}
                          </span>
                        </th>
                        <th className="text-center" style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedOrders().length > 0 ? (
                        getSortedOrders().map((order) => (
                          <tr
                            key={order.id}
                            onClick={() => openOrderPopup(order)}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: order.status === 'pending'
                                ? 'rgba(241, 196, 15, 0.25)'
                                : order.status === 'preparing'
                                  ? 'rgba(52, 152, 219, 0.25)'
                                  : order.status === 'completed'
                                    ? 'rgba(46, 204, 113, 0.25)'
                                    : order.status === 'done'
                                      ? 'rgba(201, 193, 236, 0.6)'
                                      : order.status === 'cancelled'
                                        ? 'rgba(231, 76, 60, 0.5)'
                                        : 'inherit'
                            }}
                            className={`order-row order-status-${order.status}`}
                          >
                            <td>{order.id}</td>
                            <td>
                              {order.customer ? (
                                <span
                                  className="copyable-text"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Ngừng bubbling để không mở popup
                                    if (order.customer?.phoneNumber) {
                                      handleCopy(order.customer.phoneNumber, 'số điện thoại')(e);
                                    }
                                  }}
                                  title={order.customer.phoneNumber ? "Nhấp để sao chép số điện thoại" : ""}
                                >
                                  {order.customer.name || order.customer.phoneNumber || 'Không có thông tin'}
                                </span>
                              ) : (
                                order.customerName ?
                                  <span>{order.customerName}</span> :
                                  order.customerId ?
                                    `ID: ${order.customerId}` :
                                    'Không có khách hàng'
                              )}
                            </td>
                            <td className="d-none d-md-table-cell">{order.product}</td>
                            <td className="d-none d-md-table-cell">{order.quantity}</td>
                            <td className="d-none d-md-table-cell">{formatCurrency(order.price)}</td>
                            <td>
                              {formatUTCToLocal(order.deliveryDate, 'HH:mm DD/MM')}
                            </td>
                            <td className="text-center">
                              {order.address && order.address.trim() !== '' && (
                                <i className="fa-solid fa-truck-fast" title={order.address}></i>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">
                            Không tìm thấy đơn hàng nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Popup Modal */}
      {showPopup && selectedOrder && (
        <div className="order-popup-overlay" onClick={closePopup}>
          <div className="order-popup-container" onClick={(e) => e.stopPropagation()}>

            <div className="order-popup-body">
              <div className="popup-content">
                <div className="customer-info">
                  <h6 className="popup-section-title">
                    <button type="button" className="close-btn" onClick={closePopup} style={{ float: 'right', marginTop: '-10px' }}>
                      <i className="fa fa-times"></i>
                    </button>
                    <i className="fa-solid fa-user mr-1"></i>
                    {
                      selectedOrder.customer?.name ||
                      selectedOrder.customerName ||
                      'Không có thông tin'
                    }

                    {selectedOrder.customer?.phoneNumber && (
                      <span
                        className="copyable-text"
                        onClick={handleCopy(selectedOrder.customer?.phoneNumber, 'số điện thoại')}
                      >
                        {" "}{selectedOrder.customer?.phoneNumber}
                      </span>
                    )}
                  </h6>
                </div>

                <div className="product-info">
                  {selectedOrder.imagePath && (
                    <div className="product-image">
                      <img
                        src={getImageUrl(selectedOrder.imagePath)}
                        alt={selectedOrder.product}
                        className="img-fluid rounded"
                        onClick={handleImageClick(getImageUrl(selectedOrder.imagePath) || '')}
                        onDoubleClick={handleImageDoubleClick(getImageUrl(selectedOrder.imagePath) || '')}
                        style={{ cursor: 'pointer' }}
                        title={isMobile ? "Tap để phóng to" : "Double click để phóng to"}
                      />
                    </div>
                  )}
                  <div className="product-details">
                    <div className="product-meta">
                      <p><strong>Sản phẩm:</strong>
                        <span className="product-name" style={{ paddingLeft: '5px' }}>
                          {selectedOrder.product}
                        </span>
                      </p>

                      <p><strong>Số lượng:</strong>
                        <span className="product-name" style={{ paddingLeft: '5px' }}>
                          {selectedOrder.quantity}
                        </span>
                      </p>
                      <p><strong>Đơn giá:</strong>
                        <span className="product-name" style={{ paddingLeft: '5px' }}>
                          {formatCurrency(selectedOrder.price)}
                        </span>
                      </p>


                      <p><strong>Cần thanh toán: </strong>
                        <span className="product-name" style={{ paddingLeft: '5px', color: '#FC4242', fontWeight: 'bold' }}>
                          {formatCurrency(selectedOrder.remainingAmount)}
                        </span>
                      </p>
                      {selectedOrder.remarks?.trim() && (
                        <p>
                          <strong>Lưu ý: </strong>
                          <span className="product-name" style={{ paddingLeft: '5px' }}>
                            {selectedOrder.remarks}
                          </span>
                        </p>
                      )}

                      {selectedOrder.address?.trim() && (
                        <p>
                          <strong>Địa chỉ: </strong>
                          <span className="product-name" style={{ paddingLeft: '5px' }}>
                            {selectedOrder.address}
                          </span>
                        </p>
                      )}

                    </div>
                  </div>
                </div>

                <div className="order-status-section">
                  <h6 className="popup-section-title">Trạng thái đơn hàng</h6>

                  {/* Desktop view - select dropdown */}
                  <div className="input-group d-none d-md-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-tag"></i>
                      </span>
                    </div>
                    <select
                      className="form-select form-control"
                      value={selectedOrder.status}
                      onChange={handleStatusChange}
                      disabled={updatingStatus}
                    >
                      <option value="pending">Chờ xử lý</option>
                      <option value="preparing">Đang xử lý</option>
                      <option value="done">Sẵn sàng giao</option>
                      <option value="completed">Hoàn thành</option>
                    </select>
                    {updatingStatus && (
                      <div className="input-group-append">
                        <span className="input-group-text">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Đang cập nhật...</span>
                          </div>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Mobile view - buttons */}
                  <div className="d-md-none status-buttons-container">
                    <div className="order-status-buttons">
                      <button
                        className={`status-button preparing ${selectedOrder.status === 'preparing' ? 'active' : ''}`}
                        onClick={() => !updatingStatus && selectedOrder.status !== 'preparing' && handleStatusChange({ target: { value: 'preparing' } } as React.ChangeEvent<HTMLSelectElement>)}
                        disabled={updatingStatus}
                        style={{ backgroundColor: selectedOrder.status === 'preparing' ? '#3498db' : '' }}
                      >
                        Đang xử lý
                      </button>
                      <button
                        className={`status-button done ${selectedOrder.status === 'done' ? 'active' : ''}`}
                        onClick={() => !updatingStatus && selectedOrder.status !== 'done' && handleStatusChange({ target: { value: 'done' } } as React.ChangeEvent<HTMLSelectElement>)}
                        disabled={updatingStatus}
                        style={{ backgroundColor: selectedOrder.status === 'done' ? '#C9C1EC' : '' }}
                      >
                        Sẵn sàng giao
                      </button>
                      <button
                        className={`status-button completed ${selectedOrder.status === 'completed' ? 'active' : ''}`}
                        onClick={() => !updatingStatus && selectedOrder.status !== 'completed' && handleStatusChange({ target: { value: 'completed' } } as React.ChangeEvent<HTMLSelectElement>)}
                        disabled={updatingStatus}
                        style={{ backgroundColor: selectedOrder.status === 'completed' ? '#2ecc71' : '' }}
                      >
                        Hoàn thành
                      </button>

                    </div>

                    {updatingStatus && (
                      <div className="update-status-spinner">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="sr-only">Đang cập nhật...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-center mt-8">
                  <Link to={`${userPrefix}/orders/edit/${selectedOrder.id}`} className="btn btn-info btn-lg col-md-4 mb-8" style={{ fontSize: '16px', padding: '12px 24px' }} >
                    <i className="nc-icon nc-zoom-split mr-1"></i> Xem chi tiết
                  </Link>
                </div>

                {/* Thông báo đã copy */}
                {copyMessage && (
                  <div className="copy-notification">
                    {copyMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal phóng to hình ảnh */}
      {showImageModal && selectedImageUrl && (
        <div className="image-zoom-overlay" onClick={closeImageModal}>
          <div className="image-zoom-container" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-zoom-close-btn"
              onClick={closeImageModal}
              title="Đóng (ESC)"
            >
              <i className="fa fa-times"></i>
            </button>
            <img
              src={selectedImageUrl}
              alt="Hình ảnh phóng to"
              className="image-zoom-img"
            />
            <div className="image-zoom-info">
              <p>Nhấn ESC hoặc click bên ngoài để đóng</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList; 